/**
 * Behavior Tab
 *
 * Behavioral pattern analysis - size impact, momentum, long/short comparison.
 */

import { formatCurrency, cn } from "@/lib/utils";
import { useTradingAnalysis } from "../trading-analysis-context";
import { AnalysisCard } from "./shared/analysis-card";
import { ComparisonBar, ProgressBar } from "./shared/mini-heatmap";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { formatDuration } from "../hooks";
import {
  TrendingUpIcon,
  TrendingDownIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  ActivityIcon,
} from "lucide-react";

export function BehaviorTab() {
  const { filteredTrades, behavior, stats } = useTradingAnalysis();

  const isEmpty = !filteredTrades.length;

  const consistencyRating = getConsistencyRating(behavior.sizeConsistencyScore);
  const overtradingRating = getOvertradingRating(behavior.overtradingScore);

  return (
    <div className="space-y-4">
      {/* Long vs Short */}
      <AnalysisCard title="Long vs Short Performance" subtitle="Discover your directional strengths">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Long Stats */}
          <div className="space-y-3 p-3 bg-bid/5 rounded-lg border border-bid/20">
            <div className="flex items-center gap-2">
              <TrendingUpIcon className="size-4 text-bid-foreground" />
              <span className="text-sm font-semibold">Long Positions</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] text-muted-foreground">P&L</div>
                <div
                  className={cn(
                    "text-lg font-bold",
                    isEmpty ? "text-foreground" : (behavior.longStats.pnl >= 0
                      ? "text-bid-foreground"
                      : "text-ask-foreground")
                  )}
                >
                  {isEmpty ? "$0" : (
                    <>
                      {behavior.longStats.pnl >= 0 ? "+" : ""}$
                      {formatCurrency(behavior.longStats.pnl, 0)}
                    </>
                  )}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground">Win Rate</div>
                <div className="text-lg font-bold text-foreground">
                  {isEmpty ? "0%" : `${behavior.longStats.winRate.toFixed(0)}%`}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground">Trades</div>
                <div className="text-sm font-medium">
                  {isEmpty ? 0 : behavior.longStats.trades}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground">
                  Profit Factor
                </div>
                <div className="text-sm font-medium">
                  {isEmpty ? "0.00" : (behavior.longStats.profitFactor === Infinity
                    ? "∞"
                    : behavior.longStats.profitFactor.toFixed(2))}
                </div>
              </div>
            </div>
          </div>

          {/* Short Stats */}
          <div className="space-y-3 p-3 bg-ask/5 rounded-lg border border-ask/20">
            <div className="flex items-center gap-2">
              <TrendingDownIcon className="size-4 text-ask-foreground" />
              <span className="text-sm font-semibold">Short Positions</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] text-muted-foreground">P&L</div>
                <div
                  className={cn(
                    "text-lg font-bold",
                    isEmpty ? "text-foreground" : (behavior.shortStats.pnl >= 0
                      ? "text-bid-foreground"
                      : "text-ask-foreground")
                  )}
                >
                  {isEmpty ? "$0" : (
                    <>
                      {behavior.shortStats.pnl >= 0 ? "+" : ""}$
                      {formatCurrency(behavior.shortStats.pnl, 0)}
                    </>
                  )}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground">Win Rate</div>
                <div className="text-lg font-bold text-foreground">
                  {isEmpty ? "0%" : `${behavior.shortStats.winRate.toFixed(0)}%`}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground">Trades</div>
                <div className="text-sm font-medium">
                  {isEmpty ? 0 : behavior.shortStats.trades}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground">
                  Profit Factor
                </div>
                <div className="text-sm font-medium">
                  {isEmpty ? "0.00" : (behavior.shortStats.profitFactor === Infinity
                    ? "∞"
                    : behavior.shortStats.profitFactor.toFixed(2))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Long/Short Ratio Bar */}
        <div className="mt-4">
          <ComparisonBar
            leftLabel="Longs"
            rightLabel="Shorts"
            leftValue={isEmpty ? 0 : behavior.longStats.trades}
            rightValue={isEmpty ? 0 : behavior.shortStats.trades}
          />
        </div>
        {/* Directional Insight */}
        <div className="text-[10px] text-muted-foreground mt-3 pt-3 border-t border-border/30">
          {behavior.longStats.pnl > behavior.shortStats.pnl && behavior.longStats.pnl > 0 ? (
            <span>You're more profitable going long. Consider increasing long exposure in bullish markets.</span>
          ) : behavior.shortStats.pnl > behavior.longStats.pnl && behavior.shortStats.pnl > 0 ? (
            <span>You're more profitable shorting. Consider increasing short exposure in bearish markets.</span>
          ) : (
            <span>Understanding your directional bias helps align trades with market conditions.</span>
          )}
        </div>
      </AnalysisCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Trade Size Impact */}
        <AnalysisCard title="Trade Size Impact" subtitle="Does size affect your performance?">
          <div className="space-y-2 relative">
            {behavior.sizeImpact.map((bucket) => {
              const maxTrades = Math.max(
                ...behavior.sizeImpact.map((b) => b.trades),
                1
              );
              const barWidth = isEmpty ? 0 : (bucket.trades / maxTrades) * 100;

              return (
                <div key={bucket.label} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">{bucket.label}</span>
                    <span
                      className={cn(
                        "text-xs font-semibold",
                        isEmpty ? "text-foreground" : (bucket.pnl >= 0 ? "text-bid-foreground" : "text-ask-foreground")
                      )}
                    >
                      {isEmpty ? "$0" : `${bucket.pnl >= 0 ? "+" : ""}$${formatCurrency(bucket.pnl, 0)}`}
                    </span>
                  </div>
                  <div className="h-4 bg-muted/30 rounded overflow-hidden">
                    <div
                      className={cn(
                        "h-full transition-all",
                        isEmpty ? "bg-muted-foreground/20" : (bucket.pnl >= 0 ? "bg-bid-foreground/50" : "bg-ask-foreground/50")
                      )}
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span>{isEmpty ? 0 : bucket.trades} trades</span>
                    <span>{isEmpty ? "0" : bucket.winRate.toFixed(0)}% win rate</span>
                  </div>
                </div>
              );
            })}
            {isEmpty && (
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-xs text-muted-foreground bg-card/80 px-3 py-1 rounded">Trade size data will appear here</p>
              </div>
            )}
          </div>
          <div className="text-[10px] text-muted-foreground mt-3 pt-3 border-t border-border/30">
            If larger trades underperform, consider scaling in gradually or reducing max position size.
          </div>
        </AnalysisCard>

        {/* Momentum Psychology */}
        <AnalysisCard
          title="Trading Psychology"
          subtitle="How previous outcomes affect your decisions"
        >
          <div className="space-y-4">
            {/* After Win */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  Win Rate After a Win
                </span>
                <span className="text-sm font-bold text-foreground">
                  {isEmpty ? "0.0%" : `${behavior.winRateAfterWin.toFixed(1)}%`}
                </span>
              </div>
              <ProgressBar
                value={isEmpty ? 0 : behavior.winRateAfterWin}
                variant="default"
              />
            </div>

            {/* After Loss */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  Win Rate After a Loss
                </span>
                <span className="text-sm font-bold text-foreground">
                  {isEmpty ? "0.0%" : `${behavior.winRateAfterLoss.toFixed(1)}%`}
                </span>
              </div>
              <ProgressBar
                value={isEmpty ? 0 : behavior.winRateAfterLoss}
                variant="default"
              />
            </div>

            {/* Insight */}
            <div className="p-2 bg-muted/30 rounded text-[10px] text-muted-foreground">
              {isEmpty ? (
                <span>
                  Complete trades to see how your psychology affects performance.
                </span>
              ) : behavior.winRateAfterLoss > behavior.winRateAfterWin ? (
                <span>
                  You tend to recover well after losses. Keep that discipline!
                </span>
              ) : behavior.winRateAfterWin > behavior.winRateAfterLoss + 10 ? (
                <span>
                  Your win rate drops significantly after losses. Consider taking
                  breaks after losing trades.
                </span>
              ) : (
                <span>
                  Your performance is consistent regardless of previous outcomes.
                </span>
              )}
            </div>
          </div>
        </AnalysisCard>
      </div>

      {/* Discipline Scores */}
      <AnalysisCard title="Trading Discipline" subtitle="Habits that impact your edge">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Position Sizing Consistency */}
          <div
            className={cn(
              "p-3 rounded-lg border",
              consistencyRating.variant === "good"
                ? "bg-bid/5 border-bid/20"
                : consistencyRating.variant === "bad"
                ? "bg-ask/5 border-ask/20"
                : "bg-muted/20 border-border/30"
            )}
            title="Measures how consistent your position sizes are. Lower variance = more disciplined sizing."
          >
            <div className="flex items-center gap-2 mb-2">
              {consistencyRating.variant === "good" ? (
                <CheckCircleIcon className="size-4 text-bid-foreground" />
              ) : consistencyRating.variant === "bad" ? (
                <AlertTriangleIcon className="size-4 text-ask-foreground" />
              ) : (
                <ActivityIcon className="size-4 text-muted-foreground" />
              )}
              <span className="text-xs font-medium">Size Consistency</span>
            </div>
            <div className="text-lg font-bold">{consistencyRating.label}</div>
            <div className="text-[10px] text-muted-foreground mt-1">
              Variance: {behavior.sizeConsistencyScore.toFixed(0)}%
            </div>
          </div>

          {/* Overtrading Score */}
          <div
            className={cn(
              "p-3 rounded-lg border",
              overtradingRating.variant === "good"
                ? "bg-bid/5 border-bid/20"
                : overtradingRating.variant === "bad"
                ? "bg-ask/5 border-ask/20"
                : "bg-muted/20 border-border/30"
            )}
            title="Risk of trading too frequently, often after losses. Lower is better."
          >
            <div className="flex items-center gap-2 mb-2">
              {overtradingRating.variant === "good" ? (
                <CheckCircleIcon className="size-4 text-bid-foreground" />
              ) : overtradingRating.variant === "bad" ? (
                <AlertTriangleIcon className="size-4 text-ask-foreground" />
              ) : (
                <ActivityIcon className="size-4 text-muted-foreground" />
              )}
              <span className="text-xs font-medium">Overtrading Risk</span>
            </div>
            <div className="text-lg font-bold">{overtradingRating.label}</div>
            <div className="text-[10px] text-muted-foreground mt-1">
              Score: {behavior.overtradingScore.toFixed(0)}%
            </div>
          </div>

          {/* Trade Frequency */}
          <div className="p-3 rounded-lg border bg-muted/20 border-border/30">
            <div className="flex items-center gap-2 mb-2">
              <ActivityIcon className="size-4 text-muted-foreground" />
              <TooltipPrimitive.Provider delayDuration={250}>
                <TooltipPrimitive.Root>
                  <TooltipPrimitive.Trigger asChild>
                    <span className="text-xs font-medium border-b border-dashed border-muted-foreground/50 cursor-default select-none">
                      Trade Frequency
                    </span>
                  </TooltipPrimitive.Trigger>
                  <TooltipPrimitive.Portal>
                    <TooltipPrimitive.Content
                      side="top"
                      sideOffset={5}
                      className={cn(
                        "z-50 max-w-[280px] rounded-md px-3 py-2",
                        "bg-card/95 text-foreground/90 backdrop-blur-sm",
                        "border border-border dark:border-muted-foreground/20 shadow-lg",
                        "animate-in fade-in-0 zoom-in-95",
                        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
                        "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
                      )}
                    >
                      <p className="text-xs leading-relaxed">Your average trading frequency. Higher isn't always better — quality over quantity.</p>
                    </TooltipPrimitive.Content>
                  </TooltipPrimitive.Portal>
                </TooltipPrimitive.Root>
              </TooltipPrimitive.Provider>
            </div>
            <div className="text-lg font-bold">
              {isEmpty ? "0.0" : stats.tradesPerDay.toFixed(1)}/day
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              Avg duration: {isEmpty ? "0s" : formatDuration(behavior.avgDurationMs)}
            </div>
          </div>
        </div>
        <div className="text-[10px] text-muted-foreground mt-4 pt-3 border-t border-border/30">
          Consistent position sizing and avoiding overtrading are key habits of successful traders.
        </div>
      </AnalysisCard>
    </div>
  );
}

function getConsistencyRating(score: number): {
  label: string;
  variant: "good" | "neutral" | "bad";
} {
  if (score < 30) return { label: "Excellent", variant: "good" };
  if (score < 50) return { label: "Good", variant: "good" };
  if (score < 70) return { label: "Moderate", variant: "neutral" };
  return { label: "Inconsistent", variant: "bad" };
}

function getOvertradingRating(score: number): {
  label: string;
  variant: "good" | "neutral" | "bad";
} {
  if (score < 20) return { label: "Low", variant: "good" };
  if (score < 50) return { label: "Moderate", variant: "neutral" };
  return { label: "High", variant: "bad" };
}

